/* Native progressive delivery: keep the exact resolution, decode before full download. */
(() => {
  window.TrauemyBrandFilm?.destroy();
  const instances = new Map();
  const clamp = value => Math.max(0, Math.min(1, value));
  function mount(root) {
    if (instances.has(root)) return;
    const video = root.querySelector('video');
    if (!video) return;
    const stage = root.querySelector('.tm-brand-film__stage');
    const intro = root.querySelector('.tm-brand-film__intro');
    const sunWash = document.createElement('div');
    sunWash.className = 'tm-film-sun-wash';
    sunWash.setAttribute('aria-hidden', 'true');
    stage.append(sunWash);
    const bar = root.querySelector('.tm-brand-film__progress span');
    const status = root.querySelector('[data-film-status]');
    const loadingLabel = root.querySelector('[data-film-loading]');
    const meter = root.querySelector('.tm-film-status__meter');
    const retryButton = root.querySelector('[data-film-retry]');
    const portrait = matchMedia('(max-width:767px), (min-width:768px) and (max-aspect-ratio:6/5)');
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    let dead = false, active = false, loaded = false, scrubbed = false;
    let raf = 0, stallTimer = 0, source = '', target = 0, revision = 0, nativeRetries = 0;
    let playPending = false, unlocked = false, anchorProgress = 0, anchorTime = 0, lastScroll = scrollY;
    function selectedSource() {
      const base = portrait.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
      const fourK = portrait.matches ? video.getAttribute('data-mobile-4k-src') : video.getAttribute('data-desktop-4k-src');
      // Browser toolbar height changes must not reload or lower the selected quality.
      return (!portrait.matches || innerWidth * (devicePixelRatio || 1) >= 1080) && fourK ? fourK : base;
    }
    function inView() {
      const rect = stage.getBoundingClientRect();
      return !document.hidden && rect.bottom > 0 && rect.top < innerHeight;
    }
    function state(value) {
      root.dataset.filmState = value;
      if (!status) return;
      status.hidden = value === 'ready' || value === 'static';
      const retry = value === 'retry';
      loadingLabel.hidden = retry;
      meter.hidden = retry;
      retryButton.hidden = !retry;
    }
    function clearStall() { clearTimeout(stallTimer); stallTimer = 0; }
    function desiredTime() {
      const end = Math.max(0, video.duration - .05);
      const time = Math.min(anchorTime, end);
      if (target >= anchorProgress) return time + clamp((target - anchorProgress) / Math.max(.001, 1 - anchorProgress)) * (end - time);
      return time * clamp(target / Math.max(.001, anchorProgress));
    }
    function seek() {
      if (!active || !loaded || dead || video.seeking || !Number.isFinite(video.duration)) return;
      if (!scrubbed) return;
      const next = desiredTime();
      if (Math.abs(video.currentTime - next) > .045) {
        try { video.currentTime = next; } catch { /* wait for the next metadata/seek event */ }
      }
    }
    function buffering() {
      if (!active || dead || !inView() || stallTimer) return;
      stallTimer = setTimeout(() => {
        stallTimer = 0;
        if (!active || dead || !inView()) return;
        if (video.readyState < 2 || video.seeking) state('retry');
      }, 15000);
    }
    function update() {
      raf = 0;
      if (!active || dead) return;
      const start = root.getBoundingClientRect().top + scrollY;
      const distance = Math.max(1, root.offsetHeight - stage.offsetHeight);
      target = clamp((scrollY - start) / (distance * .88));
      bar.style.transform = `scaleX(${target})`;
      root.style.setProperty('--halo-opacity', (.12 + .46 * Math.sin(Math.PI * target * .8)).toFixed(3));
      // Exposure follows the outgoing stage, while the next section is already entering.
      const exposure = clamp((scrollY - start - distance) / Math.max(1, stage.offsetHeight * .75));
      root.style.setProperty('--sun-exposure', (exposure * exposure * (3 - 2 * exposure)).toFixed(4));
      root.style.setProperty('--halo-scale', (.9 + .7 * target).toFixed(3));
      root.style.setProperty('--halo-x', `${(-8 + 18 * target).toFixed(2)}%`);
      root.style.setProperty('--halo-y', `${(9 - 20 * target).toFixed(2)}%`);
      root.style.setProperty('--film-copy-y', `${(-8 * target).toFixed(1)}px`);
      root.style.setProperty('--film-sheen', `${(110 - 210 * target).toFixed(2)}%`);
      root.style.setProperty('--film-light-x', `${(-18 + 42 * target).toFixed(2)}%`);
      root.style.setProperty('--film-light-y', `${(8 - 20 * target).toFixed(2)}%`);
      root.style.setProperty('--film-light-opacity', (.28 + .25 * Math.sin(Math.PI * target)).toFixed(3));
      if (inView()) seek();
      else if (!video.paused) video.pause();
    }
    function queue() { if (!raf && !dead) raf = requestAnimationFrame(update); }
    function onScroll() {
      const moved = Math.abs(scrollY - lastScroll) > 2;
      lastScroll = scrollY;
      if (active && !scrubbed && moved && inView()) {
        anchorProgress = target;
        anchorTime = video.currentTime || 0;
        scrubbed = true; video.loop = false; video.pause();
        root.dataset.filmMode = 'scroll';
      }
      queue();
    }
    function unlock() {
      if (!active || dead || playPending || !inView()) return;
      if (scrubbed) { video.pause(); seek(); return; }
      if (!video.paused || video.ended) return;
      video.loop = false; video.playbackRate = .7;
      const version = revision;
      playPending = true;
      video.muted = true;
      const attempt = video.play();
      Promise.resolve(attempt).then(() => {
        if (dead || version !== revision) return;
        playPending = false; unlocked = true;
        if (scrubbed) { video.pause(); seek(); }
        if (loaded) state('ready');
      }).catch(() => {
        if (dead || version !== revision) return;
        playPending = false;
        // WebViews/low-power mode can require a real tap; keep the sharp frame visible.
        state('retry');
      });
    }
    function firstFrame() {
      if (!active || dead) return;
      loaded = true;
      video.classList.add('is-ready');
      clearStall(); state('ready');
      if (!scrubbed) unlock();
      else { video.pause(); seek(); }
      queue();
    }
    function afterSeek() {
      if (dead || !active) return;
      if (video.readyState >= 2) { clearStall(); state('ready'); }
      seek();
    }
    function load(force = false) {
      if (!active || dead) return;
      video.preload = 'auto'; video.muted = true; video.playsInline = true;
      // Reuse the parser-started request. Never fetch a Blob or wait for the whole MP4.
      if (force || video.getAttribute('src') !== source) {
        loaded = false; unlocked = false; playPending = false;
        video.classList.remove('is-ready');
        state('loading'); video.src = source; video.load();
      }
      if (video.readyState >= 2) firstFrame();
      else buffering();
      unlock();
    }
    function failed() {
      if (!active || dead) return;
      loaded = false; clearStall(); video.classList.remove('is-ready');
      // A single retry on a transport error, using the same high-resolution URL.
      if (video.error?.code === 2 && nativeRetries++ === 0) load(true);
      else state('retry');
    }
    function retry() {
      unlocked = false; playPending = false;
      if (video.error || video.readyState < 2 || video.seeking) load(true);
      else { state('ready'); unlock(); seek(); }
    }
    function configure() {
      const next = selectedSource();
      const enabled = !reduced.matches && Boolean(next);
      if (active === enabled && source === next) { queue(); return; }
      revision++; clearStall(); nativeRetries = 0; unlocked = false; playPending = false;
      active = enabled; source = next; scrubbed = false; anchorProgress = 0; anchorTime = 0; lastScroll = scrollY;
      video.loop = false; video.playbackRate = .7; root.dataset.filmMode = 'autoplay';
      root.dataset.filmSource = source || '';
      root.dataset.filmBuffered = 'progressive';
      root.classList.toggle('is-scroll-film', active);
      if (!active) {
        video.pause(); video.removeAttribute('src'); video.load();
        video.classList.remove('is-ready'); root.style.removeProperty('--film-copy-y');
        state('static'); return;
      }
      // One slow pass; retain the last frame until scrolling takes control.
      queue(); load();
    }
    const observer = new IntersectionObserver(() => {
      if (inView()) { unlock(); queue(); }
      else video.pause();
    });
    observer.observe(stage);
    const resize = new ResizeObserver(queue); resize.observe(stage); resize.observe(intro);
    const onResize = () => { if (source !== selectedSource()) configure(); else queue(); };
    const onVisibility = () => { if (document.hidden) video.pause(); else { unlock(); queue(); } };
    video.addEventListener('loadeddata', firstFrame);
    video.addEventListener('canplay', firstFrame);
    video.addEventListener('seeked', afterSeek);
    video.addEventListener('waiting', buffering);
    video.addEventListener('stalled', buffering);
    video.addEventListener('error', failed);
    retryButton?.addEventListener('click', retry);
    reduced.addEventListener('change', configure);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('touchstart', unlock, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    configure(); document.fonts?.ready.then(() => { if (!dead) queue(); });
    instances.set(root, () => {
      dead = true; revision++; clearStall(); observer.disconnect(); resize.disconnect(); sunWash.remove();
      cancelAnimationFrame(raf); video.pause();
      video.removeEventListener('loadeddata', firstFrame); video.removeEventListener('canplay', firstFrame);
      video.removeEventListener('seeked', afterSeek); 
      video.removeEventListener('waiting', buffering); video.removeEventListener('stalled', buffering);
      video.removeEventListener('error', failed); retryButton?.removeEventListener('click', retry);
      reduced.removeEventListener('change', configure); window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize); window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock); document.removeEventListener('visibilitychange', onVisibility);
      root.classList.remove('is-scroll-film');
    });
  }
  function init() { document.querySelectorAll('[data-brand-film]').forEach(mount); }
  function unload(event) { for (const [root, clean] of instances) { if (event.target.contains(root)) { clean(); instances.delete(root); } } }
  function restore(event) { if (event.persisted) { instances.forEach(clean => clean()); instances.clear(); init(); } }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', unload);
  window.addEventListener('pageshow', restore);
  window.TrauemyBrandFilm = { init, destroy() {
    instances.forEach(clean => clean()); instances.clear();
    document.removeEventListener('shopify:section:load', init);
    document.removeEventListener('shopify:section:unload', unload); window.removeEventListener('pageshow', restore);
  } };
  init();
})();
